/**
 * Stores: the record everything else hangs off.
 *
 * A store carries the brand, the IANA timezone every schedule decision is made
 * in, and the opening hours that decide when a screen shows its closed state.
 * Creating and changing them is admin-only, which the page shows by explaining
 * rather than by hiding the list from managers who still need to read it.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StoreUpsert } from '@pbtv/shared';
import { PageHeader } from '../components/PageHeader.js';
import { Modal } from '../components/Modal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { ErrorBlock, InfoNote, InlineEmpty, LoadingBlock } from '../components/Feedback.js';
import { StoreForm } from '../components/stores/StoreForm.js';
import { errorMessage } from '../lib/api.js';
import { canManageStores, hasNoScope } from '../lib/access.js';
import { formatRelativeTime } from '../lib/format.js';
import {
  DAY_NAMES,
  ISO_DAYS,
  describeDay,
  summariseOpeningHours,
  toWeek,
} from '../lib/openingHours.js';
import type { StoreRecord } from '../lib/schemas.js';
import { FLEET_QUERY_KEY, listFleet } from '../lib/screens.js';
import { useSession } from '../lib/session.js';
import { STORES_QUERY_KEY, createStore, deleteStore, listStores, updateStore } from '../lib/stores.js';
import { describeTimezone } from '../lib/timezones.js';

type Dialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; store: StoreRecord }
  | { kind: 'delete'; store: StoreRecord };

export function StoresPage(): ReactElement {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const mayManage = canManageStores(user);

  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const closeDialog = (): void => setDialog({ kind: 'none' });

  const stores = useQuery({
    queryKey: STORES_QUERY_KEY,
    queryFn: ({ signal }) => listStores(signal),
  });

  // Shares its cache entry with the screens page. A store cannot be deleted
  // while screens are filed under it, so knowing the count before opening the
  // delete dialog is the difference between a warning and a 409.
  const fleet = useQuery({
    queryKey: FLEET_QUERY_KEY,
    queryFn: ({ signal }) => listFleet(signal),
    retry: 0,
  });

  const screenCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of fleet.data ?? []) {
      counts.set(row.storeId, (counts.get(row.storeId) ?? 0) + 1);
    }
    return counts;
  }, [fleet.data]);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY });
  };

  const create = useMutation({
    mutationFn: (value: StoreUpsert) => createStore(value),
    onSuccess: async () => {
      await invalidate();
      closeDialog();
    },
  });

  const update = useMutation({
    mutationFn: ({ storeId, value }: { storeId: string; value: StoreUpsert }) =>
      updateStore(storeId, value),
    onSuccess: async () => {
      await invalidate();
      closeDialog();
    },
  });

  const remove = useMutation({
    mutationFn: (storeId: string) => deleteStore(storeId),
    onSuccess: async () => {
      await invalidate();
      closeDialog();
    },
  });

  const rows = useMemo(() => {
    const list = stores.data ?? [];
    return [...list].sort((left, right) => left.name.localeCompare(right.name));
  }, [stores.data]);

  const knownBrands = useMemo(() => rows.map((store) => store.brand), [rows]);

  const openCreate = (): void => {
    create.reset();
    setDialog({ kind: 'create' });
  };

  const openEdit = (store: StoreRecord): void => {
    update.reset();
    setDialog({ kind: 'edit', store });
  };

  const openDelete = (store: StoreRecord): void => {
    remove.reset();
    setDialog({ kind: 'delete', store });
  };

  return (
    <>
      <PageHeader
        title="Stores"
        description="Each store carries the brand, the IANA timezone every schedule is evaluated in, and the opening hours that decide when a screen goes dark."
      >
        {mayManage ? (
          <button type="button" className="button button--primary" onClick={openCreate}>
            New store
          </button>
        ) : null}
      </PageHeader>

      {!mayManage ? (
        <InfoNote>
          Stores are read-only for store managers. Ask an admin to change a name, a timezone or a
          set of opening hours.
        </InfoNote>
      ) : null}

      {stores.isPending ? <LoadingBlock label="Loading stores…" /> : null}

      {stores.isError ? (
        <ErrorBlock error={stores.error} action="load the stores" onRetry={() => void stores.refetch()} />
      ) : null}

      {!stores.isPending && !stores.isError && rows.length === 0 ? (
        // A manager with no stores assigned sees an empty list for a completely
        // different reason than an admin on a fresh install does, and telling
        // them "no stores yet" would point them at work they cannot do.
        hasNoScope(user) ? (
          <InlineEmpty
            title="No stores assigned to you yet"
            description="Your account has not been given access to a store. An administrator needs to add you to one before anything appears here."
          />
        ) : (
          <InlineEmpty
            title="No stores yet"
            description="Nothing can be scheduled until a store exists, because a daypart is meaningless without the store’s own clock."
          >
            {mayManage ? (
              <button type="button" className="button button--primary" onClick={openCreate}>
                Create the first store
              </button>
            ) : null}
          </InlineEmpty>
        )
      ) : null}

      {rows.map((store) => (
        <StoreCard
          key={store.storeId}
          store={store}
          screenCount={screenCounts.get(store.storeId) ?? store.screenCount ?? null}
          mayManage={mayManage}
          onEdit={() => openEdit(store)}
          onDelete={() => openDelete(store)}
        />
      ))}

      <Modal
        open={dialog.kind === 'create' || dialog.kind === 'edit'}
        size="wide"
        title={dialog.kind === 'edit' ? `Edit ${dialog.store.name}` : 'New store'}
        description="Opening hours may run past midnight — enter the closing time as it is written on the door."
        onClose={closeDialog}
      >
        {dialog.kind === 'create' ? (
          <StoreForm
            key="create"
            initial={null}
            knownBrands={knownBrands}
            busy={create.isPending}
            error={create.error}
            onSubmit={(value) => create.mutate(value)}
            onCancel={closeDialog}
          />
        ) : null}

        {dialog.kind === 'edit' ? (
          <StoreForm
            key={dialog.store.storeId}
            initial={dialog.store}
            knownBrands={knownBrands}
            busy={update.isPending}
            error={update.error}
            onSubmit={(value) => update.mutate({ storeId: dialog.store.storeId, value })}
            onCancel={closeDialog}
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        open={dialog.kind === 'delete'}
        title={dialog.kind === 'delete' ? `Delete ${dialog.store.name}?` : 'Delete store?'}
        description="The store, its opening hours and its schedule settings are removed."
        confirmLabel="Delete store"
        busyLabel="Deleting…"
        busy={remove.isPending}
        error={remove.error === null ? null : errorMessage(remove.error)}
        onConfirm={() => {
          if (dialog.kind === 'delete') remove.mutate(dialog.store.storeId);
        }}
        onClose={closeDialog}
      >
        {dialog.kind === 'delete' ? (
          <StoreDeleteWarning count={screenCounts.get(dialog.store.storeId) ?? 0} />
        ) : null}
      </ConfirmDialog>
    </>
  );
}

function StoreDeleteWarning({ count }: { count: number }): ReactElement {
  if (count === 0) {
    return (
      <p className="state-block__text">
        No screens are filed under this store. If one is paired between now and the delete, the
        server will refuse and say so.
      </p>
    );
  }

  return (
    <p className="alert alert--warning" role="status">
      {count === 1 ? 'One screen is' : `${count} screens are`} still filed under this store. Un-pair{' '}
      {count === 1 ? 'it' : 'them'} on the Screens page first — the server refuses to delete a store
      that still owns a screen.
    </p>
  );
}

interface StoreCardProps {
  store: StoreRecord;
  /** Null while the fleet has not been read; the card then says nothing about screens. */
  screenCount: number | null;
  mayManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function StoreCard({
  store,
  screenCount,
  mayManage,
  onEdit,
  onDelete,
}: StoreCardProps): ReactElement {
  const week = useMemo(() => toWeek(store.openingHours), [store.openingHours]);

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h3 className="panel__title">
            {store.name} <span className="chip chip--neutral">{store.brand}</span>
          </h3>
          <p className="panel__subtitle">
            {store.address === '' ? 'No address recorded' : store.address}
          </p>
        </div>

        {mayManage ? (
          <div className="panel__actions">
            <button type="button" className="button button--ghost" onClick={onEdit}>
              Edit
            </button>
            <button type="button" className="button button--ghost" onClick={onDelete}>
              Delete
            </button>
          </div>
        ) : null}
      </div>

      <dl className="facts">
        <div>
          <dt>Timezone</dt>
          <dd>
            {store.timezone}
            <span className="facts__aside"> · {describeTimezone(store.timezone, Date.now())}</span>
          </dd>
        </div>
        <div>
          <dt>Opening hours</dt>
          <dd>{summariseOpeningHours(store.openingHours)}</dd>
        </div>
        {screenCount !== null ? (
          <div>
            <dt>Screens</dt>
            <dd>{screenCount === 1 ? '1 paired' : `${screenCount} paired`}</dd>
          </div>
        ) : null}
        {typeof store.updatedAt === 'string' ? (
          <div>
            <dt>Last changed</dt>
            <dd>{formatRelativeTime(store.updatedAt, Date.now())}</dd>
          </div>
        ) : null}
      </dl>

      <details className="disclosure">
        <summary className="disclosure__summary">Hours day by day</summary>
        <dl className="day-list">
          {ISO_DAYS.map((day) => (
            <div className="day-list__row" key={day}>
              <dt>{DAY_NAMES[day]}</dt>
              <dd>{describeDay(week[day - 1]?.periods ?? [])}</dd>
            </div>
          ))}
        </dl>
      </details>
    </section>
  );
}
